"use client";

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api, { User, Team } from './api';

interface AuthContextType {
  user: User | null;
  teams: Team[];
  currentTeam: Team | null;
  setCurrentTeam: (team: Team) => void;
  refreshTeams: () => Promise<void>;
  loading: boolean;
  logout: () => Promise<void>;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null, teams: [], currentTeam: null, setCurrentTeam: () => {}, refreshTeams: async () => {}, loading: true, logout: async () => {}, isAdmin: false
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [currentTeam, setCurrentTeam] = useState<Team | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUser();
  }, []);

  async function loadUser() {
    try {
      const [me, teamList] = await Promise.all([api.getMe(), api.getTeams()]);
      setUser(me);
      setTeams(teamList || []);
      // Use saved team or first team
      const savedTeamId = localStorage.getItem('currentTeamId');
      const saved = teamList.find(t => t.id === savedTeamId);
      setCurrentTeam(saved || teamList[0] || null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  function handleSetCurrentTeam(team: Team) {
    setCurrentTeam(team);
    localStorage.setItem('currentTeamId', team.id);
  }

  async function refreshTeams() {
    try {
      const teamList = await api.getTeams();
      setTeams(teamList || []);
      // Keep current team if still valid, otherwise pick first
      const current = currentTeam;
      const stillValid = current && teamList.find(t => t.id === current.id);
      if (!stillValid) {
        const saved = teamList[0] || null;
        setCurrentTeam(saved);
        if (saved) localStorage.setItem('currentTeamId', saved.id);
      }
    } catch {
      // Ignore refresh errors
    }
  }

  async function handleLogout() {
    await api.logout();
    setUser(null);
    window.location.href = '/login';
  }

  const isAdmin = user?.is_admin ?? false;

  return (
    <AuthContext.Provider value={{ user, teams, currentTeam, setCurrentTeam: handleSetCurrentTeam, refreshTeams, loading, logout: handleLogout, isAdmin }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
